import authService from './authService';

export interface ContentSubject {
  subject_id: string;
  subject_name: string;
  is_project?: boolean;
}

export interface ContentTopic {
  topic_id: string;
  topic_name: string;
  topic_alt_name?: string;
  topic_description?: string;
  description?: string;
  subject_id?: string;
  subject_name?: string;
  del_row?: boolean;
}

/** Prefer topic_description, then alt name / legacy description fields. */
export function getTopicDescription(topic: ContentTopic): string {
  return String(
    topic.topic_description ||
      topic.description ||
      topic.topic_alt_name ||
      '',
  ).trim();
}

export const fetchSubjectsForTrack = async (
  trackId: string,
  options?: { excludeProject?: boolean }
): Promise<ContentSubject[]> => {
  const excludeProject = options?.excludeProject ?? false;
  const response = await authService.get(
    `admin/content/subjects_for_topics/${trackId}/`,
    { params: { exclude_project: excludeProject } }
  );
  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Failed to fetch subjects');
  }
  return response.data.subjects || [];
};

export const fetchTopicsForSubject = async (
  subjectId: string
): Promise<ContentTopic[]> => {
  const response = await authService.get(
    `admin/content/get_all_topics/${subjectId}/`
  );
  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Failed to fetch topics');
  }
  return response.data.topics || [];
};

export interface ContentSubtopic {
  sub_topic_id: string;
  sub_topic_name: string;
  topic_id?: string;
  topic_name?: string;
}

export const fetchSubtopicsForTopic = async (
  topicId: string
): Promise<ContentSubtopic[]> => {
  const response = await authService.get(
    `admin/content/get_all_subTopics/${topicId}/`
  );
  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Failed to fetch subtopics');
  }
  return response.data.sub_topics || [];
};

export type LevelCountMap = {
  level1: number;
  level2: number;
  level3: number;
};

export const fetchQuestionCountsBySubtopic = async (
  subTopicId: string
): Promise<{ mcq: LevelCountMap; coding: LevelCountMap }> => {
  const response = await authService.post(
    'admin/content/get_questions_data_by_subtopic/',
    { sub_topic_id: subTopicId }
  );
  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Failed to fetch question counts');
  }
  return {
    mcq: response.data.mcq || { level1: 0, level2: 0, level3: 0 },
    coding: response.data.coding || { level1: 0, level2: 0, level3: 0 },
  };
};

const emptyLevelCounts = (): LevelCountMap => ({
  level1: 0,
  level2: 0,
  level3: 0,
});

export const fetchQuestionCountsBySubtopics = async (
  subTopicIds: string[]
): Promise<Record<string, { mcq: LevelCountMap; coding: LevelCountMap }>> => {
  const ids = Array.from(new Set(subTopicIds.filter(Boolean)));
  if (!ids.length) return {};

  const response = await authService.post(
    'admin/content/get_questions_data_by_subtopics/',
    { sub_topic_ids: ids }
  );
  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Failed to fetch question counts');
  }

  const data = (response.data?.data || {}) as Record<
    string,
    { mcq?: LevelCountMap; coding?: LevelCountMap }
  >;
  const result: Record<string, { mcq: LevelCountMap; coding: LevelCountMap }> = {};
  for (const sid of ids) {
    const row = data[sid];
    result[sid] = {
      mcq: row?.mcq || emptyLevelCounts(),
      coding: row?.coding || emptyLevelCounts(),
    };
  }
  return result;
};
